import { FC, useState } from 'react'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Link, Paper, Typography, alpha, useTheme } from '@mui/material'
import Brand from '../../../Assets/brand.webp'
import { DialogChangePassword } from '../../../Components/Dialog/variants/dialog-change-password'
import { Placeholder } from '../../../Components/Placeholder/placeholder.component'
import { useToast } from '../../../Hooks/ToasContext'
import { ConfirmPasswordResetUseCase } from '../../../Usecase/Auth/confirm-password-reset-usecase'
import { adminVersion } from '../../../Services/api'

interface IResetPasswordPageProps {
  confirmPasswordResetUseCase: ConfirmPasswordResetUseCase
}

const ResetPasswordPage: FC<IResetPasswordPageProps> = ({
  confirmPasswordResetUseCase,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')?.trim() || ''
  const [loading, setLoading] = useState(false)

  const handleConfirm = async ({ password }: { password: string }) => {
    setLoading(true)

    try {
      const result = await confirmPasswordResetUseCase.handle({
        token,
        newPassword: password,
      })

      if (result.isFailure) {
        const err = result.error as { error?: string; message?: string; statusCode?: number }
        if (err?.statusCode === 404) {
          showToast('Recuperação de senha ainda não disponível no servidor. Entre em contato com o administrador.', 'error')
          return
        }
        showToast(String(err?.error || err?.message || 'Não foi possível redefinir a senha.'), 'error')
        return
      }

      showToast('Senha redefinida com sucesso. Faça login com a nova senha.', 'success')
      navigate('/signin', { replace: true })
    } catch {
      showToast('Não foi possível redefinir a senha. Tente novamente.', 'error')
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          backgroundColor: '#eef2e6',
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 480, width: '100%', borderRadius: 3 }}>
          <Placeholder
            description="Link de recuperação inválido. Verifique o e-mail recebido."
            Bottom={() => (
              <Link component={RouterLink} to="/signin" sx={{ mt: 2 }}>
                Voltar para o login
              </Link>
            )}
          />
        </Paper>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#eef2e6',
        backgroundImage: `
          radial-gradient(circle at 15% 20%, ${alpha(theme.palette.primary.main, 0.18)} 0%, transparent 42%)
        `,
      }}
    >
      <DialogChangePassword
        open
        forceReset
        isLoading={loading}
        title="Redefinir senha"
        description="Defina uma nova senha para acessar a plataforma. Ela deve ter no mínimo 6 caracteres, incluindo números, uma letra maiúscula e um caractere especial."
        onConfirm={handleConfirm}
        onCancel={() => navigate('/signin')}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ position: 'fixed', bottom: 16, left: 0, right: 0, textAlign: 'center' }}
      >
        Versão {adminVersion}
      </Typography>
    </Box>
  )
}

export { ResetPasswordPage }
